import { ArrowUpRight } from "lucide-react";
import { Seo, breadcrumbSchema, orgSchema } from "@/components/layout/Seo";
import { PageHero, Reveal, SectionHead } from "@/components/ui/motion";
import { VALUES } from "@/data/services";
import { TeamGrid } from "@/components/sections/TeamGrid";
import { SITE } from "@/lib/config";

const MISSIONS = [
  ["Pendampingan profesional", "Memberikan konsultasi dan pendampingan dengan standar kerja yang jelas, terdokumentasi, dan bertanggung jawab."],
  ["Transparansi di setiap langkah", "Menjelaskan opsi, biaya, dan ketentuan sejak awal sehingga klien dapat mengambil keputusan dengan tenang."],
  ["Berpusat pada klien", "Mendengarkan kondisi setiap klien dan menyesuaikan pendekatan dengan kebutuhan serta kemampuannya."],
  ["Edukasi finansial", "Membantu masyarakat memahami pengelolaan kewajiban agar dapat membangun kestabilan finansial jangka panjang."],
];

// Source: public team section of mitrakaryalawoffice.com
const LEADERS = [
  {
    name: "Muhamad Indra Gunawan, S.H., M.H., C.HL., C.PS., C.Med",
    role: "Legal Consultant",
    photo: "/images/team/indragunawan-mitrakaryalawoffice.webp",
    bio: "Alumni S1 UNPAM (2020) dan S2 Mathla'ul Anwar Banten (2023). Managing Partner di Law Office Muhamad Indra Gunawan & Partner serta Legal Consultant di sejumlah perusahaan, dengan portofolio non-litigasi, legal drafting, legal opinion, dan konsultasi hukum.",
  },
  {
    name: "Muhammad Irgi Fahriji",
    role: "Direktur",
    photo: "/images/team/irgi.webp",
    bio: "Penggerak di balik Mitra Karya Law Office. Dengan keyakinan bahwa setiap orang berhak mendapatkan kesempatan kedua dalam kebebasan finansial, beliau memimpin tim untuk memberikan layanan yang solutif sekaligus empatik.",
  },
];

export default function About() {
  const crumbs = [{ to: "/", label: "Beranda" }, { to: "/tentang-kami", label: "Tentang Kami" }];
  return (
    <>
      <Seo title="Tentang Kami | Mika Solution" description="Kenali Mika Solution, layanan konsultasi dan pendampingan penyelesaian kewajiban finansial yang merupakan bagian dari Mitra Karya Law Office." path="/tentang-kami" schema={[orgSchema, breadcrumbSchema(crumbs)]} />
      <PageHero crumbs={crumbs} eyebrow="Tentang Mika Solution" lines={["Solusi yang Berawal", <>dari <span className="serif-it font-normal text-accentb">Pemahaman.</span></>]} desc="Mika Solution membantu masyarakat memahami kondisi kewajiban finansial, mengevaluasi pilihan penyelesaian yang tersedia, serta menjalani proses pendampingan secara lebih terarah dan transparan." />

      <section className="py-20 sm:py-28" data-testid="about-relationship">
        <div className="wrap grid grid-cols-1 gap-14 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-[1.5rem] rounded-bl-[9rem]"><img src="/images/sign.jpg" alt="Penandatanganan dokumen kesepakatan" className="h-full w-full object-cover" /></div>
          </Reveal>
          <div className="lg:col-span-6 lg:col-start-7">
            <SectionHead eyebrow="Hubungan Perusahaan" title={<>Bagian dari <span className="serif-it font-normal text-accentb">{SITE.parent.name}.</span></>} />
            <Reveal delay={0.1}>
              <p className="mt-8 text-[17px] leading-[1.8] text-ink">MIKA SOLUTION merupakan bagian dari {SITE.parent.name}. Mika Solution berfokus pada konsultasi dan pendampingan penyelesaian kewajiban pinjaman online, kartu kredit, dan Kredit Tanpa Agunan (KTA) bagi masyarakat.</p>
              <p className="mt-5 text-[17px] leading-[1.8] text-ink">Kami percaya setiap orang berhak memahami kondisinya secara utuh sebelum mengambil keputusan, dan berhak didampingi dengan cara yang empatik serta bermartabat.</p>
              <a href={SITE.parent.url} target="_blank" rel="noopener noreferrer" className="btn btn-ghost mt-8" data-testid="about-parent-site">Kunjungi situs {SITE.parent.name} <ArrowUpRight size={16} /></a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-ice py-20 sm:py-28" data-testid="about-vision-mission">
        <div className="wrap grid grid-cols-1 gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <span className="eyebrow">Visi</span>
            <Reveal><p className="mt-6 font-display text-3xl font-bold leading-[1.15] text-slateb sm:text-4xl">Menjadi mitra pendampingan yang dipercaya masyarakat dalam memahami dan menyelesaikan kewajiban finansial secara <span className="serif-it font-normal text-accentb">transparan dan bermartabat.</span></p></Reveal>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <span className="eyebrow">Misi</span>
            <ol className="mt-6 border-t border-line">
              {MISSIONS.map(([t, d], i) => (
                <Reveal key={t} delay={i * 0.05}><li className="grid grid-cols-[48px_1fr] gap-4 border-b border-line py-6"><span className="serif-it text-3xl text-accentb">0{i + 1}</span><span><span className="block font-display text-lg font-bold text-slateb">{t}</span><span className="mt-1.5 block text-[15px] leading-relaxed text-ink">{d}</span></span></li></Reveal>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="py-20 sm:py-28" data-testid="about-values">
        <div className="wrap">
          <SectionHead eyebrow="Nilai Kami" title="Prinsip yang kami pegang." />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}><article className="card h-full p-7"><span className="font-display text-sm font-bold text-accentb">0{i + 1}</span><h3 className="mt-6 font-display text-xl font-bold">{v.title}</h3><p className="mt-3 text-[15px] leading-relaxed text-ink">{v.text}</p></article></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white pb-20 sm:pb-28" data-testid="about-team">
        <div className="wrap">
          <SectionHead eyebrow="Pimpinan" title="Orang-orang di balik layanan kami." desc={`Profil berikut bersumber dari situs resmi ${SITE.parent.name}.`} />
          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            {LEADERS.map((m, i) => (
              <Reveal key={m.name} delay={i * 0.08}>
                <article className="card group grid h-full overflow-hidden sm:grid-cols-[42%_1fr]" data-testid={`leader-card-${i}`}>
                  <div className="relative m-3 aspect-[4/5] overflow-hidden rounded-[1.1rem] rounded-tl-[5rem] bg-ice sm:mr-0">
                    <img src={m.photo} alt={`Foto ${m.name}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1.4s] group-hover:scale-[1.05]" />
                  </div>
                  <div className="flex flex-col p-6 sm:p-8">
                    <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-accentb">{m.role}</span>
                    <h3 className="mt-3 font-display text-xl font-bold leading-snug sm:text-2xl">{m.name}</h3>
                    <p className="mt-4 text-[14.5px] leading-relaxed text-ink">{m.bio}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-20"><span className="eyebrow">Tim Pendamping — klik untuk melihat profil &amp; WhatsApp</span></Reveal>
          <div className="mt-8"><TeamGrid /></div>
        </div>
      </section>
    </>
  );
}
